import React from 'react';
import {
  fetchRollups, fetchPrices, fetchFxRates, fetchManualCosts, fetchTelemetry,
  computeCost, convertToINR,
} from '@/lib/ops-data.js';

// OpsData context — loads the cost core (rollups + prices + fx + manual costs +
// telemetry) once per date range and shares it across screens. Money is computed
// at read time here so every screen aggregates ready-made native + INR amounts.
const Ctx = React.createContext(null);

export function useOps() {
  const v = React.useContext(Ctx);
  if (!v) throw new Error('useOps must be used within <OpsProvider>');
  return v;
}

export function OpsProvider({ range, children }) {
  const [state, setState] = React.useState({ loading: true, error: null, data: null });
  const [tick, setTick] = React.useState(0);
  const reload = React.useCallback(() => setTick((t) => t + 1), []);

  React.useEffect(() => {
    let alive = true;
    setState((s) => ({ ...s, loading: true, error: null }));
    (async () => {
      try {
        const [rollups, prices, fx, manualCosts, telemetry] = await Promise.all([
          fetchRollups(range),
          fetchPrices(),
          fetchFxRates(),
          fetchManualCosts(),
          fetchTelemetry(range),
        ]);
        if (!alive) return;
        setState({ loading: false, error: null, data: { rollups, prices, fx, manualCosts, telemetry } });
      } catch (e) {
        if (!alive) return;
        setState({ loading: false, error: e?.message || String(e), data: null });
      }
    })();
    return () => { alive = false; };
  }, [range.from, range.to, tick]);

  const value = React.useMemo(() => {
    const d = state.data;
    if (!d) return { ...state, range, reload };
    const byKey = d.prices.byKey;
    const unconvertible = new Set();
    // Enrich each rollup with native cost + INR cost (honest null when unpriced or
    // unconvertible).
    const enriched = d.rollups.map((r) => {
      const cost = computeCost(r, byKey);
      const inr = cost.priced ? convertToINR(cost.amount, cost.currency, r.day, d.fx) : null;
      if (cost.priced && cost.currency !== 'INR' && inr === null) unconvertible.add(cost.currency);
      return { ...r, cost, inr };
    });
    return {
      loading: false,
      error: null,
      range,
      reload,
      rollups: d.rollups,
      enriched,
      prices: d.prices,      // { list, byKey }
      byKey,
      fx: d.fx,
      manualCosts: d.manualCosts,
      telemetry: d.telemetry,
      unconvertible: [...unconvertible],
    };
  }, [state, range, reload]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
